// src/components/eventos/components/operacional/TarefaCard.js
import React from 'react';

const formatarTempo = (segundos) => {
    const mins = Math.floor(Math.abs(segundos) / 60);
    const secs = Math.abs(segundos) % 60;
    const sinal = segundos < 0 ? '-' : '';
    return `${sinal}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const CheckIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const SmCheckIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
);
const MapPinIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
);
const UserIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
);

const TarefaCard = ({ tarefa, numero, tempoInfo, tarefaRef, onConcluirTarefa }) => {
    const { decorrido, restante, duracao } = tempoInfo;
    const progresso = Math.min(100, Math.max(0, (decorrido / duracao) * 100));
    const atrasado = restante < 0;
    const concluida = tarefa.status === 'concluida';
    const emAndamento = tarefa.status === 'em_andamento';

    const accentColor = emAndamento ? (atrasado ? '#ef4444' : '#2CCB90') : null;

    return (
        <>
            <style>{`
                @keyframes tc-spin { to { transform: rotate(360deg); } }
                @keyframes tc-pulse-green {
                    0%,100% { box-shadow: 0 4px 24px rgba(44,203,144,0.15), 0 1px 4px rgba(44,203,144,0.08); }
                    50%     { box-shadow: 0 6px 32px rgba(44,203,144,0.28), 0 2px 8px rgba(44,203,144,0.14); }
                }
                @keyframes tc-pulse-red {
                    0%,100% { box-shadow: 0 4px 24px rgba(239,68,68,0.15), 0 1px 4px rgba(239,68,68,0.08); }
                    50%     { box-shadow: 0 6px 32px rgba(239,68,68,0.28), 0 2px 8px rgba(239,68,68,0.14); }
                }
                @keyframes tc-dot { 0%,100%{transform:scale(1);opacity:1;} 50%{transform:scale(.75);opacity:.5;} }
            `}</style>

            <div
                ref={tarefaRef}
                style={{
                    background: emAndamento
                        ? (atrasado ? 'linear-gradient(135deg,#fff5f5 0%,#fffafa 100%)' : 'linear-gradient(135deg,#f0fdf8 0%,#f8fffc 100%)')
                        : concluida ? '#fafafa' : '#ffffff',
                    border: emAndamento
                        ? `1px solid ${atrasado ? 'rgba(239,68,68,0.3)' : 'rgba(44,203,144,0.3)'}`
                        : '1px solid #e8edf2',
                    borderLeft: emAndamento
                        ? `4px solid ${accentColor}`
                        : concluida ? '4px solid #e2e8f0' : '4px solid transparent',
                    borderRadius: '14px',
                    padding: '18px 18px 18px 16px',
                    opacity: concluida ? 0.72 : 1,
                    transition: 'all 0.2s',
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    animation: emAndamento ? (atrasado ? 'tc-pulse-red 2.5s ease-in-out infinite' : 'tc-pulse-green 2.5s ease-in-out infinite') : 'none',
                }}
            >
                {/* ── Header ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {/* Número */}
                    <span style={{
                        padding: '3px 9px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                        background: concluida ? '#f1f5f9' : emAndamento ? (atrasado ? 'rgba(239,68,68,0.08)' : 'rgba(44,203,144,0.1)') : 'rgba(139,92,246,0.08)',
                        border: `1px solid ${concluida ? '#e2e8f0' : emAndamento ? (atrasado ? 'rgba(239,68,68,0.2)' : 'rgba(44,203,144,0.2)') : 'rgba(139,92,246,0.18)'}`,
                        color: concluida ? '#94a3b8' : emAndamento ? accentColor : '#7c3aed',
                    }}>
                        #{numero}
                    </span>

                    {/* Título */}
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: concluida ? '#94a3b8' : '#0f172a', flex: 1, textDecoration: concluida ? 'line-through' : 'none', textDecorationColor: '#cbd5e1' }}>
                        {tarefa.titulo}
                    </h3>

                    {/* Badge status */}
                    {emAndamento && !atrasado && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '100px', background: '#2CCB90', color: '#fff', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', boxShadow: '0 2px 8px rgba(44,203,144,0.35)' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff', animation: 'tc-dot 1s ease-in-out infinite', flexShrink: 0 }} />
                            EM ANDAMENTO
                        </span>
                    )}
                    {atrasado && !concluida && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '100px', background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', boxShadow: '0 2px 8px rgba(239,68,68,0.35)' }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff', animation: 'tc-dot 0.8s ease-in-out infinite', flexShrink: 0 }} />
                            ATRASADO
                        </span>
                    )}
                    {concluida && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '100px', background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', fontSize: '10px', fontWeight: 700 }}>
                            <SmCheckIcon /> CONCLUÍDA
                        </span>
                    )}
                </div>

                {/* ── Meta info ── */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: emAndamento ? accentColor : '#64748b', background: emAndamento ? (atrasado ? 'rgba(239,68,68,0.07)' : 'rgba(44,203,144,0.07)') : 'transparent', padding: emAndamento ? '2px 7px' : '0', borderRadius: '6px' }}>
                        <ClockIcon /> {tarefa.horaInicio} – {tarefa.horaFim}
                    </span>
                    <span style={{ padding: '2px 8px', borderRadius: '6px', background: 'rgba(45,184,225,0.07)', border: '1px solid rgba(45,184,225,0.15)', color: '#0891b2', fontSize: '11px', fontWeight: 600 }}>
                        {tarefa.duracao} min
                    </span>
                    {tarefa.responsavel && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                            <UserIcon /> {tarefa.responsavel}
                        </span>
                    )}
                    {tarefa.local && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                            <MapPinIcon /> {tarefa.local}
                        </span>
                    )}
                </div>

                {/* Início real */}
                {tarefa.horaInicioReal && (
                    <p style={{ margin: '0 0 2px', fontSize: '12px', color: '#2CCB90', fontWeight: 500 }}>
                        ✓ Início real: {tarefa.horaInicioReal}
                        {tarefa.horaFimReal && ` · Fim: ${tarefa.horaFimReal}`}
                    </p>
                )}

                {/* Descrição texto */}
                {(tarefa.descricao_tipo === 'texto' || (!tarefa.descricao_tipo && tarefa.descricao)) && tarefa.descricao && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.12)', borderLeft: '3px solid #a78bfa', borderRadius: '8px' }}>
                        <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Descrição</p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>{tarefa.descricao}</p>
                    </div>
                )}

                {/* Descrição lista */}
                {tarefa.descricao_tipo === 'lista' && tarefa.descricao_items?.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: 'rgba(44,203,144,0.05)', border: '1px solid rgba(44,203,144,0.12)', borderLeft: '3px solid #2CCB90', borderRadius: '8px' }}>
                        <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, color: '#2CCB90', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Itens</p>
                        {tarefa.descricao_items.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', padding: '3px 0', fontSize: '13px', color: '#64748b', borderBottom: i < tarefa.descricao_items.length - 1 ? '1px solid rgba(44,203,144,0.08)' : 'none' }}>
                                <span style={{ color: '#2CCB90', marginTop: '1px', flexShrink: 0 }}><SmCheckIcon /></span>
                                {item}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Timer em andamento ── */}
                {emAndamento && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${atrasado ? 'rgba(239,68,68,0.15)' : 'rgba(44,203,144,0.15)'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ fontSize: 'clamp(2.8rem,6vw,3.8rem)', fontWeight: 900, color: atrasado ? '#ef4444' : '#2CCB90', lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                                    {formatarTempo(restante)}
                                </div>
                                <p style={{ margin: '6px 0 0', fontSize: '11px', fontWeight: 700, color: atrasado ? '#ef4444' : '#2CCB90', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                    {atrasado ? '⚠ tempo em atraso' : 'tempo restante'}
                                </p>
                            </div>
                            {onConcluirTarefa && (
                                <button
                                    onClick={() => onConcluirTarefa(tarefa.id)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '12px 24px', borderRadius: '10px', background: 'linear-gradient(135deg, #2CCB90, #22a876)', border: 'none', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(44,203,144,0.3)', transition: 'all 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(44,203,144,0.4)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(44,203,144,0.3)'; }}
                                >
                                    <CheckIcon /> Concluir Tarefa
                                </button>
                            )}
                        </div>

                        {/* Barra de progresso */}
                        <div style={{ marginTop: '14px' }}>
                            <div style={{ height: '8px', borderRadius: '100px', background: '#e2e8f0', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${progresso}%`, borderRadius: '100px', background: atrasado ? 'linear-gradient(90deg,#f87171,#ef4444)' : 'linear-gradient(90deg,#2CCB90,#2DB8E1)', transition: 'width 1s linear' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                                <span style={{ fontSize: '11px', color: atrasado ? '#ef4444' : '#2CCB90', fontWeight: 600 }}>{Math.round(progresso)}% concluído</span>
                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{Math.floor(decorrido / 60)}:{(decorrido % 60).toString().padStart(2, '0')} decorridos</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TarefaCard;
